import React, { useState, useEffect  } from 'react';
import '../../styles/socioeconomicoStyle/DadosCarrossel.css';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { socioeconomicoService, RegiaoItem, MunicipioResponse } from '../../API/socioeconomicoService';

interface Subitem {
  label: string;
  valor: string | number;
  subitens?: Subitem[] ;
}

interface FiltrosProps {
  filtros: {
    estado: string;
    municipio: RegiaoItem | null;
  } | null;
}


export interface Dado {
  titulo?: string;
  dados: {
    label: string;
    valor: string | number;
    subitens?: { label: string; valor: string | number }[];
  }[];
}

const formatarPercentual = (valor: number): string => {
  const percentual = valor / 100;
  return `${percentual.toFixed(1)}%`;
};

export const montarDadosCarrossel = (m: MunicipioResponse): Dado[] => [
  {
    titulo: 'Informações básicas',
    dados: [
      {
        label: 'Habitantes',
        valor: m.habitantes.toLocaleString(),
        subitens: [
          { label: 'Homens', valor: m.homens.toLocaleString() },
          { label: 'Mulheres', valor: m.mulheres.toLocaleString() },
          { label: 'Casados', valor: m.casados.toLocaleString() },
        ],
      },
      {
        label: 'Área',
        valor: m.area.toLocaleString(),
        subitens: [
          { label: 'Densidade populacional', valor: `${m.densidade_pop.toLocaleString()} hab./km²` },
          { label: 'Densidade habitacional', valor: `${m.densidade_dom.toLocaleString()} dom./km²` },
        ],
      },
    ],
  },
  {
    titulo: 'Economia',
    dados: [
      { label: 'Produto Interno Bruto (PIB)', 
        valor: m.pib.toLocaleString() 
      },
      { label: 'Índice de Desenvolvimento Humano (IDH)', valor: m.idh.toLocaleString() },
    ],
  },
  {
    titulo: 'Domicílios',
    dados: [
      { label: 'Total', valor: m.domicilios.toLocaleString() },
      { label: 'Apartamentos', valor: m.apartamentos.toLocaleString() },
      { label: 'Alugados', valor: m.alugados.toLocaleString() },
      { label: 'Em favela', valor: m.em_favela.toLocaleString() },
      { label: 'Média de pessoas por domicílio', valor: m.pessoas_por_domicilio.toLocaleString() },
      {
        label: 'Distribuição por domicílio',
        valor: '',
        subitens: [
          { label: '1 pessoa', valor: m.dom_1_pessoa.toLocaleString() },
          { label: '2 pessoas', valor: m.dom_2_pessoas.toLocaleString() },
          { label: '3 pessoas', valor: m.dom_3_pessoas.toLocaleString() },
          { label: '4 pessoas', valor: m.dom_4_pessoas.toLocaleString() },
          { label: '5 pessoas ou mais', valor: m.dom_5mais_pessoas.toLocaleString() },
        ],
      },
    ],
  },
  {
    titulo: 'Domicílios por classe de renda',
    dados: [
      {
        label: 'Classe A',
        valor: m.classe_a.toLocaleString(),
        subitens: [
          { label: 'A1', valor: m.a1.toLocaleString() },
          { label: 'A2', valor: m.a2.toLocaleString() },
        ],
      },
      {
        label: 'Classe B',
        valor: m.classe_b.toLocaleString(),
        subitens: [
          { label: 'B1', valor: m.b1.toLocaleString() },
          { label: 'B2', valor: m.b2.toLocaleString() },
        ],
      },
      {
        label: 'Classe C',
        valor: m.classe_c.toLocaleString(),
        subitens: [
          { label: 'C1', valor: m.c1.toLocaleString() },
          { label: 'C2', valor: m.c2.toLocaleString() },
        ],
      },
      {
        label: 'Classe D/E',
        valor: m.classe_de.toLocaleString(),
        subitens: [
          { label: 'D', valor: m.d.toLocaleString() },
          { label: 'E', valor: m.e.toLocaleString() },
        ],
      },
    ],
  },
  {
    titulo: 'Renda bruta mensal domiciliar',
    dados: [
      { 
        label: 'Renda bruta mensal por domicílio', 
        valor: Number(m.media_por_domicilio).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) 
      },
      {
        label: 'Classe A',
        valor: Number(m.media_mensal_classe_a).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
        subitens: [
          { label: 'A1', valor: Number(m.media_mensal_a1).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
          { label: 'A2', valor: Number(m.media_mensal_a2).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
        ],
      },
      {
        label: 'Classe B',
        valor: m.media_mensal_classe_b != null 
          ? Number(m.media_mensal_classe_b).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) 
          : "R$ 0,00",
        subitens: [
          { label: 'B1', valor: Number(m.media_mensal_b1).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
          { label: 'B2', valor: Number(m.media_mensal_b2).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
        ],
      },
      {
        label: 'Classe C',
        valor: Number(m.media_mensal_classe_c).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
        subitens: [
          { label: 'C1', valor: Number(m.media_mensal_c1).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
          { label: 'C2', valor: Number(m.media_mensal_c2).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
        ],
      },
      {
        label: 'Classe D/E',
        valor: Number(m.media_mensal_classe_de).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
        subitens: [
          { label: 'D', valor: Number(m.media_mensal_d).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
          { label: 'E', valor: Number(m.media_mensal_e).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
        ],
      },
    ],
  },
  {
    titulo: 'Renda mensal',
    dados: [
      { label: 'Média per capita', valor: Number(m.media_per_capita).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
      { label: 'Média por domicílio', valor: Number(m.media_por_domicilio).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
      {
        label: 'Classe A',
        valor: Number(m.media_mensal_classe_a).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
        subitens: [
          { label: 'A1', valor: Number(m.media_mensal_a1).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
          { label: 'A2', valor: Number(m.media_mensal_a2).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
        ],
      },
      {
        label: 'Classe B',
        valor: m.media_mensal_classe_b != null 
          ? Number(m.media_mensal_classe_b).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) 
          : "R$ 0,00",
        subitens: [
          { label: 'B1', valor: Number(m.media_mensal_b1).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
          { label: 'B2', valor: Number(m.media_mensal_b2).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
        ],
      },
      {
        label: 'Classe C',
        valor: Number(m.media_mensal_classe_c).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
        subitens: [
          { label: 'C1', valor: Number(m.media_mensal_c1).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
          { label: 'C2', valor: Number(m.media_mensal_c2).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
        ],
      },
      {
        label: 'Classe D/E',
        valor: Number(m.media_mensal_classe_de).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
        subitens: [
          { label: 'D', valor: Number(m.media_mensal_d).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
          { label: 'E', valor: Number(m.media_mensal_e).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
        ],
      },
    ],
  },
  {
    titulo: 'Faixa etária',
    dados: [
      {
        label: 'Crianças (0 a 14 anos)',
        valor: m.crianca_total.toLocaleString(),
        subitens: [
          { label: '0 a 4 anos', valor: m.criancas_0_4.toLocaleString() },
          { label: '5 a 9 anos', valor: m.criancas_5_9.toLocaleString() },
          { label: '10 a 14 anos', valor: m.criancas_10_14.toLocaleString() },
        ],
      },
      {
        label: 'Jovens (15 a 29 anos)',
        valor: (m.jovens_15_19 + m.jovens_20_24 + m.jovens_25_29).toLocaleString(),
        subitens: [
          { label: '15 a 19 anos', valor: m.jovens_15_19.toLocaleString() },
          { label: '20 a 24 anos', valor: m.jovens_20_24.toLocaleString() },
          { label: '25 a 29 anos', valor: m.jovens_25_29.toLocaleString() },
        ],
      },
      {
        label: 'Adultos (30 a 59 anos)',
        valor: (m.adultos_30_39 + m.adultos_40_49 + m.adultos_50_59).toLocaleString(),
        subitens: [
          { label: '30 a 39 anos', valor: m.adultos_30_39.toLocaleString() },
          { label: '40 a 49 anos', valor: m.adultos_40_49.toLocaleString() },
          { label: '50 a 59 anos', valor: m.adultos_50_59.toLocaleString() },
        ],
      },
      {
        label: 'Idosos (60 anos ou mais)',
        valor: (m.idosos_60_64 + m.idosos_65_69 + m.idosos_70mais).toLocaleString(),
        subitens: [
          { label: '60 a 64 anos', valor: m.idosos_60_64.toLocaleString() },
          { label: '65 a 69 anos', valor: m.idosos_65_69.toLocaleString() },
          { label: '70 anos ou mais', valor: m.idosos_70mais.toLocaleString() },
        ],
      },
    ],
  },
  {
    titulo: 'Bens e serviços',
    dados: [
      { label: 'Domicílios com automóvel', valor: m.dom_com_automovel.toLocaleString() },
      { label: 'Domicílios com moto', valor: m.dom_com_moto.toLocaleString() },
      { label: 'Domicílios com internet', valor: m.dom_com_internet.toLocaleString() },
      { label: 'Domicílios com celular', valor: m.dom_com_celular.toLocaleString() },
    ],
  },
  {
    titulo: 'Tempo para chegar ao trabalho',
    dados: [
      { label: 'Até 5 minutos', valor: formatarPercentual(m.cinco_minutos) },
      { label: '6 a 15 minutos', valor: formatarPercentual(m.seis_a_quinze_minutos) },
      { label: '15 a 30 minutos', valor: formatarPercentual(m.quinze_a_trinta_minutos) },
      { label: '30 min a 1 hora', valor: formatarPercentual(m.trinta_minutos_a_uma_hora) },
      { label: '1 a 2 horas', valor: formatarPercentual(m.uma_a_duas_horas) },
      { label: '2 a 4 horas', valor: formatarPercentual(m.duas_a_quatro_horas) },
      { label: 'Mais de 4 horas', valor: formatarPercentual(m.mais_de_quatro_horas) },
    ],
  },
];



const DadosCarrossel: React.FC<FiltrosProps> = ({ filtros }) => {
  const [municipioData, setMunicipioData] = useState<MunicipioResponse | null>(null);
  const [index, setIndex] = useState<number>(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [popup, setPopup] = useState<{ visible: boolean; label: string }>({
    visible: false,
    label: '',
  });

  // Resetar índice e expandeds quando o município mudar
  useEffect(() => {
    const fetchDados = async () => {
      if (!filtros?.municipio?.municipioNormalizado) {
        setMunicipioData(null);
        return;
      }

      try {
        const dados = await socioeconomicoService.getDadosMunicipio(filtros.municipio);
        setMunicipioData(dados);
      } catch (error) {
        console.error("Erro ao buscar dados do município:", error);
        setMunicipioData(null);
      }
    };

    fetchDados();
    setIndex(0);
    setExpanded({});
  }, [filtros?.municipio]);


  // Dados dinâmicos do município ou vazio
  const dadosMunicipio = municipioData ? montarDadosCarrossel(municipioData) : [];
  const dadoAtual = dadosMunicipio?.[index];

  const handlePrev = () => {
    setIndex(prev => (prev === 0 ? dadosMunicipio.length - 1 : prev - 1));
    setExpanded({});
  };

  const handleNext = () => {
    setIndex(prev => (prev === dadosMunicipio.length - 1 ? 0 : prev + 1));
    setExpanded({});
  };

  const toggleExpand = (key: string) => {
    setExpanded(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleInfoClick = (label: string) => {
    const explicacoes: Record<string, string> = {
      Habitantes: 'Número total de pessoas residentes em uma determinada área geográfica.',
      Homens: 'Número total de pessoas residentes do sexo masculino.',
      Mulheres: 'Número total de pessoas residentes do sexo feminino.',
      Casados: 'Número total de pessoas casadas ou em união estável.',
      Área: 'Extensão territorial da região em km².',
      'Densidade populacional': 'Média de habitantes por km².',
      'Densidade habitacional': 'Média de domicílios por km².',
      'Produto Interno Bruto (PIB)': 'Valor total de bens e serviços produzidos na região.',
      'Índice de desenvolvimento Humano (IDH)': 'Mede o desenvolvimento humano considerando renda, educação e longevidade.',
      'Total': 'Número total de domicílios.',
      'Apartamentos': 'Quantidade de domicílios do tipo apartamento.',
      'Alugados': 'Quantidade de domicílios alugados.',
      'Em favela': 'Número de domicílios em favelas.',
      'Média de pessoas por domicílio': 'Média de habitantes por domicílio.',
      'Distribuição por domicílio': 'Distribuição de domicílios por número de moradores.',
      'Média per capita': 'Média de renda por pessoa.',
      'Média por domicílio': 'Média de renda por domicílio.',
      'Classe A': 'Renda média domiciliar da classe A.',
      'Classe B': 'Renda média domiciliar da classe B.',
      'Classe C': 'Renda média domiciliar da classe C.',
      'Classe D/E': 'Renda média domiciliar da classe D/E.',
      'Domicílios com automóvel': 'Domicílios com pelo menos um automóvel.',
      'Domicílios com moto': 'Domicílios com pelo menos uma moto.',
      'Domicílios com internet': 'Domicílios com acesso à internet.',
      'Domicílios com celular': 'Domicílios com acesso a celular.',
    };

    setPopup({
      visible: true,
      label: explicacoes[label] ?? 'Descrição não disponível.',
    });
  };

  const fecharPopup = () => {
    setPopup({ visible: false, label: '' });
  };

  const renderItem = (item: Subitem, depth = 0, path = ''): React.ReactNode => {
    const key = `${path}${item.label}`;

    return (
      <React.Fragment key={key}>
        <li className={`item level-${depth}`}>
          <span className="label">
            {item.subitens && (
              <button
                className="toggle-btn"
                onClick={() => toggleExpand(key)}
                aria-expanded={!!expanded[key]}
                aria-controls={`${key}-subitems`}
              >
                {expanded[key] ? <ChevronUp /> : <ChevronDown />}
              </button>
            )}
            {item.label}:
          </span>
          <span className="valor">
            {item.valor}
            <Info className="info-btn" onClick={() => handleInfoClick(item.label)} />
          </span>
        </li>
        {item.subitens && expanded[key] && (
          <ul id={`${key}-subitems`}>
            {item.subitens.map(sub => renderItem(sub, depth + 1, key + '/'))}
          </ul>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="carrossel-container-wrapper">
      {dadoAtual ? (
        <div className="card">
          <div className="card-header">
            <button className="nav-button left" onClick={handlePrev}>
              <ChevronLeft size={24} />
            </button>

            <h3>{dadoAtual.titulo}</h3>

            <button className="nav-button right" onClick={handleNext}>
              <ChevronRight size={24} />
            </button>
          </div>

          <ul>{dadoAtual.dados.map(item => renderItem(item))}</ul>
        </div>
      ) : (
        <div className="card">
          <p>Selecione um município para visualizar os dados.</p>
        </div>
      )}

      {/* ✅ POP-UP DE INFORMAÇÃO */}
      {popup.visible && (
        <div className="info-popup-overlay" onClick={fecharPopup}>
          <div className="info-popup" onClick={(e) => e.stopPropagation()}>
            <h4>Informação</h4>
            <p>{popup.label}</p>
            <button onClick={fecharPopup}>Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DadosCarrossel;
